import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';
const G = BRAND.colors.primary;

// Slug used by the per-supplier sync/debug endpoints, + a couple of example
// styles to nudge the user on the first pull.
const CODE_TO_SLUG = { SM: 'sanmar', SS: 'ssactivewear', GS: 'goldstar', SNUGZ: 'snugz', HIT: 'hit' };
const EXAMPLE_STYLES = { SM: 'PC61, 3001C, K420', SS: 'G500, B00760003', GS: 'LUM, MGN-001', SNUGZ: 'PC100, LB200', HIT: 'AP1000, LT4500' };


const SUPPLIER_FIELDS = {
  SM: {
    accountLabel: 'SanMar.com username',
    secretLabel:  'SanMar.com password',
    help: 'Your sanmar.com website login. SanMar returns your account-specific pricing when you authenticate as your own account. Note: SanMar also requires the server IP to be whitelisted with their integrations team.',
  },
  SS: {
    accountLabel: 'S&S account number',
    secretLabel:  'S&S API key',
    // Confirmed against S&S's own PromoStandards page: the account number +
    // API key both live on the regular ssactivewear.com account (not a
    // separate developer portal), but the API key itself is sometimes not
    // visible by default — S&S's own fallback is to email their API team.
    help: (
      <>
        Your S&amp;S Activewear account number, plus the API key from your{' '}
        <a href="https://www.ssactivewear.com/myaccount" target="_blank" rel="noreferrer" style={{ color:'inherit', textDecoration:'underline' }}>
          My Account
        </a>{' '}
        page (look under Manage Account — it may be behind a "Show" link next to the account number, not displayed by default).
        If you don't see it there, email{' '}
        <a href="mailto:api@ssactivewear.com" style={{ color:'inherit', textDecoration:'underline' }}>
          api@ssactivewear.com
        </a>{' '}
        and ask them to issue one.
      </>
    ),
  },
  GS: {
    accountLabel: 'Goldstar username',
    secretLabel:  'Goldstar password',
    help: 'Your Goldstar account credentials. Goldstar returns product data and pricing for your account.',
  },
};

export default function SupplierConnections({ dist, getToken, showToast }) {
  const [loading, setLoading] = useState(true);
  const [encryptionReady, setEncryptionReady] = useState(true);
  const [suppliers, setSuppliers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const t = await getToken();
      const data = await api.withToken(t).listSupplierCredentials();
      setEncryptionReady(data.encryption_ready !== false);
      setSuppliers(data.suppliers || []);
    } catch (e) {
      showToast('Failed to load connections: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [getToken, showToast]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ color:'#94A3B8', fontSize:13 }}>Loading…</div>;

  return (
    <div>
      <div style={{ fontSize:16, fontWeight:600, color:'#0F172A', marginBottom:6 }}>Supplier Connections</div>
      <div style={{ fontSize:13, color:'#64748B', lineHeight:1.55, marginBottom:18 }}>
        SanMar and S&amp;S Activewear return <strong>account-specific pricing</strong>, so each account connects
        its own login. Credentials are encrypted at rest and used only to fetch your pricing and product data.
      </div>

      {!encryptionReady && (
        <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', color:'#B91C1C', borderRadius:8, padding:'10px 14px', fontSize:12.5, marginBottom:16, lineHeight:1.5 }}>
          Credential storage isn't configured on the server yet (missing encryption key). Saving is disabled until that's set up.
        </div>
      )}

      <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
        {suppliers.filter(s => s.supplier_code !== 'GS' && s.supplier_code !== 'SNUGZ' && s.supplier_code !== 'HIT').map(s => (
          <SupplierCard key={s.supplier_code} supplier={s} disabled={!encryptionReady}
            dist={dist} getToken={getToken} showToast={showToast} onChanged={load} />
        ))}
      </div>
    </div>
  );
}

function SupplierCard({ supplier, disabled, dist, getToken, showToast, onChanged }) {
  const f = SUPPLIER_FIELDS[supplier.supplier_code] || { accountLabel:'Account ID', secretLabel:'Secret', help:'' };
  const [editing, setEditing]       = useState(!supplier.connected);
  const [accountId, setAccountId]   = useState(supplier.account_id || '');
  const [secret, setSecret]         = useState('');
  const [saving, setSaving]         = useState(false);
  const [testing, setTesting]       = useState(false);
  const [testResult, setTestResult] = useState(null);
  // Blocking notice shown right after a successful connect/reconnect — the
  // distributor must click OK before continuing, rather than a toast they
  // could miss. Only for this one moment; the passive progress panel below
  // (PricingBackfillStatus) handles ongoing status after they dismiss this.
  const [showSyncNotice, setShowSyncNotice] = useState(false);

  const inp = { width:'100%', border:'1px solid #E2E8F0', borderRadius:8, padding:'9px 11px', fontSize:13, fontFamily:'inherit', outline:'none', color:'#0F172A', boxSizing:'border-box' };
  const btn = (bg, color, border) => ({ background:bg, color, border:border||'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:500, cursor:'pointer', fontFamily:'inherit' });

  const save = async () => {
    if (!accountId.trim() || !secret.trim()) { showToast('Enter both fields', 'error'); return; }
    setSaving(true);
    try {
      const t = await getToken();
      await api.withToken(t).saveSupplierCredential({
        supplier_code: supplier.supplier_code,
        account_id: accountId.trim(),
        secret: secret.trim(),
      });
      // The save triggers a one-time background pull of THIS account's real
      // pricing for every product already in the catalog (thousands of
      // items for SanMar/S&S) — this is important enough to require an
      // explicit acknowledgment (a modal, not a toast that could be missed)
      // before the distributor moves on, so it's never a surprise later.
      setShowSyncNotice(true);
      setSecret(''); setEditing(false); setTestResult(null);
      onChanged();
    } catch (e) {
      showToast('Save failed: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const t = await getToken();
      const r = await api.withToken(t).testSupplierCredential(supplier.supplier_code);
      setTestResult(r);
    } catch (e) {
      setTestResult({ ok:false, error:e.message });
    } finally {
      setTesting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(`Disconnect ${supplier.supplier_name}? Pricing for their products will fall back to defaults.`)) return;
    try {
      const t = await getToken();
      await api.withToken(t).deleteSupplierCredential(supplier.supplier_code);
      showToast(`${supplier.supplier_name} disconnected`);
      setEditing(true); setAccountId(''); setSecret(''); setTestResult(null);
      onChanged();
    } catch (e) {
      showToast('Failed: ' + e.message, 'error');
    }
  };

  return (
    <>
    <div style={{ border:'1px solid #E2E8F0', borderRadius:12, padding:18 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: editing ? 12 : 4 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:14, fontWeight:600, color:'#0F172A' }}>{supplier.supplier_name}</span>
          {supplier.connected
            ? <span style={{ fontSize:11, fontWeight:600, color:'#059669', background:`${G}18`, padding:'2px 9px', borderRadius:12 }}>Connected</span>
            : <span style={{ fontSize:11, fontWeight:500, color:'#94A3B8', background:'#F1F5F9', padding:'2px 9px', borderRadius:12 }}>Not connected</span>}
        </div>
        {supplier.connected && !editing && (
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={test} disabled={testing} style={btn('#fff', '#475569', '1px solid #E2E8F0')}>{testing ? 'Testing…' : 'Test'}</button>
            <button onClick={()=>setEditing(true)} style={btn('#fff', '#475569', '1px solid #E2E8F0')}>Update</button>
            <button onClick={disconnect} style={btn('#fff', '#B91C1C', '1px solid #FECACA')}>Disconnect</button>
          </div>
        )}
      </div>

      {supplier.connected && !editing && (
        <div style={{ fontSize:12, color:'#64748B' }}>
          {f.accountLabel}: <strong style={{ color:'#475569' }}>{supplier.account_id}</strong>
        </div>
      )}

      {supplier.connected && !editing && (
        <SupplierSyncTool code={supplier.supplier_code} getToken={getToken} showToast={showToast} />
      )}

      {supplier.connected && !editing && dist?.platform_admin === true && (
        <FullCatalogSyncTool code={supplier.supplier_code} supplierName={supplier.supplier_name}
          getToken={getToken} showToast={showToast} />
      )}

      {supplier.connected && !editing && (
        <PricingBackfillStatus code={supplier.supplier_code} supplierName={supplier.supplier_name}
          getToken={getToken} />
      )}

      {testResult && (
        <div style={{ marginTop:10, fontSize:12, borderRadius:8, padding:'8px 12px', lineHeight:1.5,
          background: testResult.ok ? `${G}0D` : '#FEF2F2',
          color: testResult.ok ? '#059669' : '#B91C1C',
          border: `1px solid ${testResult.ok ? `${G}33` : '#FECACA'}` }}>
          {testResult.ok
            ? `✓ Connected — fetched ${testResult.testedStyle}${testResult.productName ? ` (${testResult.productName})` : ''} in ${testResult.elapsedMs}ms`
            : `✗ ${testResult.error || 'Test failed'}`}
        </div>
      )}

      {editing && (
        <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:10 }}>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:500, color:'#374151', marginBottom:4 }}>{f.accountLabel}</label>
            <input value={accountId} onChange={e=>setAccountId(e.target.value)} style={inp} autoComplete="off" />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:500, color:'#374151', marginBottom:4 }}>{f.secretLabel}</label>
            <input type="password" value={secret} onChange={e=>setSecret(e.target.value)}
              placeholder={supplier.connected ? 'Enter to replace the stored value' : ''} style={inp} autoComplete="new-password" />
          </div>
          {f.help && <div style={{ fontSize:11, color:'#94A3B8', lineHeight:1.5 }}>{f.help}</div>}
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={save} disabled={saving || disabled} style={btn(disabled ? '#CBD5E1' : G, '#fff')}>{saving ? 'Saving…' : 'Save & connect'}</button>
            {supplier.connected && <button onClick={()=>{ setEditing(false); setSecret(''); }} style={btn('#fff', '#475569', '1px solid #E2E8F0')}>Cancel</button>}
          </div>
        </div>
      )}
    </div>

    {showSyncNotice && (
      <div style={{
        position:'fixed', inset:0, background:'rgba(15,23,42,0.5)', zIndex:1000,
        display:'flex', alignItems:'center', justifyContent:'center', padding:20,
      }}>
        <div style={{ background:'#fff', borderRadius:12, padding:24, maxWidth:440, boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
          <div style={{ fontSize:15, fontWeight:600, color:'#0F172A', marginBottom:8 }}>
            {supplier.supplier_name} connected
          </div>
          <div style={{ fontSize:13, color:'#475569', lineHeight:1.6, marginBottom:18 }}>
            We're now pulling <strong>your account's real pricing</strong> for every {supplier.supplier_name} product
            already in the catalog — thousands of items, so it may take a few minutes to finish.
            <br /><br />
            This is a <strong>one-time sync</strong>. Once it's done, your pricing stays current automatically —
            you won't need to do this again. You can keep working while it runs in the background; progress shows
            below on this page.
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end' }}>
            <button onClick={()=>setShowSyncNotice(false)} style={btn(G, '#fff')}>OK</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
// Pull products from a connected supplier into the searchable catalog. Also lets
// the distributor preview one style's raw response (confirms real data flows,
// and is what we use to finalize the apparel size/pricing mapping).
function SupplierSyncTool({ code, getToken, showToast }) {
  const slug = CODE_TO_SLUG[code];
  const [styles, setStyles]       = useState('');
  const [syncing, setSyncing]     = useState(false);
  const [result, setResult]       = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview]     = useState(null);

  const inp = { flex:1, minWidth:0, border:'1px solid #E2E8F0', borderRadius:8, padding:'8px 11px', fontSize:13, fontFamily:'inherit', outline:'none', color:'#0F172A', boxSizing:'border-box' };
  const btn = (bg, color, border) => ({ background:bg, color, border:border||'none', borderRadius:8, padding:'8px 14px', fontSize:13, fontWeight:500, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' });

  const parseStyles = () => styles.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);

  const sync = async () => {
    const ids = parseStyles();
    if (!ids.length) { showToast('Enter one or more style numbers', 'error'); return; }
    setSyncing(true); setResult(null);
    try {
      const t = await getToken();
      const r = await api.withToken(t).syncSupplierProducts(slug, ids);
      setResult(r);
      if (r.synced > 0) showToast(`Synced ${r.synced} product${r.synced === 1 ? '' : 's'} — now searchable`);
    } catch (e) {
      showToast('Sync failed: ' + e.message, 'error');
    } finally {
      setSyncing(false);
    }
  };

  const doPreview = async () => {
    const style = parseStyles()[0];
    if (!style) { showToast('Enter a style number first', 'error'); return; }
    setPreviewing(true); setPreview(null);
    try {
      const t = await getToken();
      const r = await api.withToken(t).previewSupplierProduct(slug, style);
      setPreview(r);
    } catch (e) {
      setPreview({ ok:false, error:e.message });
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div style={{ marginTop:14, paddingTop:14, borderTop:'1px dashed #E2E8F0' }}>
      <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:6 }}>Pull products into your catalog</div>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        <input value={styles} onChange={e=>setStyles(e.target.value)}
          placeholder={`Style numbers, e.g. ${EXAMPLE_STYLES[code] || ''}`} style={inp} />
        <button onClick={sync} disabled={syncing} style={btn(G, '#fff')}>{syncing ? 'Syncing…' : 'Sync to catalog'}</button>
        <button onClick={doPreview} disabled={previewing} style={btn('#fff', '#475569', '1px solid #E2E8F0')}>{previewing ? 'Loading…' : 'Preview'}</button>
      </div>
      <div style={{ fontSize:11, color:'#94A3B8', marginTop:6, lineHeight:1.5 }}>
        Synced items appear in the product search bar when building a presentation. (Full apparel
        size-grid + your account pricing is the next step — use <strong>Preview</strong> on one
        style and share the result to finish that setup.)
      </div>

      {result && (
        <div style={{ marginTop:10, fontSize:12, borderRadius:8, padding:'8px 12px', lineHeight:1.5,
          background: result.synced > 0 ? `${G}0D` : '#FEF2F2',
          color: result.synced > 0 ? '#059669' : '#B91C1C',
          border: `1px solid ${result.synced > 0 ? `${G}33` : '#FECACA'}` }}>
          Synced {result.synced || 0}, failed {result.failed || 0}, skipped {result.skipped || 0}.
          {Array.isArray(result.errors) && result.errors.length > 0 &&
            <span> First error: {typeof result.errors[0] === 'string' ? result.errors[0] : JSON.stringify(result.errors[0])}</span>}
        </div>
      )}

      {preview && (
        <div style={{ marginTop:10 }}>
          <div style={{ fontSize:11, color:'#64748B', marginBottom:4 }}>
            {preview.ok
              ? `Preview of ${preview.productId} — ${preview.partCount} parts, ${preview.priceGroupCount} price group(s). Copy this and share it to finish apparel setup:`
              : `Preview failed: ${preview.error || 'unknown error'}`}
          </div>
          {preview.ok && (
            <textarea readOnly value={JSON.stringify(preview, null, 2)} onFocus={e=>e.target.select()}
              style={{ width:'100%', height:160, fontFamily:'monospace', fontSize:11, border:'1px solid #E2E8F0', borderRadius:8, padding:'8px 10px', color:'#334155', boxSizing:'border-box', resize:'vertical' }} />
          )}
        </div>
      )}
    </div>
  );
}

// Pull the supplier's ENTIRE catalog into the searchable products table.
// Runs in the background server-side (thousands of styles for SanMar), so
// this component polls a status endpoint rather than waiting on one request.
// Safe to leave the page and come back — status is per-distributor, stored
// server-side, not in component state. Starting again while already running
// just resumes/reattaches rather than restarting from zero.
function FullCatalogSyncTool({ code, supplierName, getToken, showToast }) {
  const slug = CODE_TO_SLUG[code];
  const [status, setStatus]     = useState(null); // null while first loading
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);

  const poll = useCallback(async () => {
    try {
      const t = await getToken();
      const s = await api.withToken(t).getFullCatalogSyncStatus(slug);
      setStatus(s);
    } catch (e) {
      // Don't spam toasts on every failed poll tick — just leave last-known status.
    }
  }, [slug, getToken]);

  useEffect(() => { poll(); }, [poll]);

  useEffect(() => {
    if (status?.status !== 'running') return;
    const id = setInterval(poll, 4000);
    return () => clearInterval(id);
  }, [status?.status, poll]);

  const start = async () => {
    setStarting(true);
    try {
      const t = await getToken();
      const r = await api.withToken(t).startFullCatalogSync(slug, 25);
      showToast(r.resumed
        ? `Resuming full ${supplierName} sync (${r.remaining} of ${r.total} left)`
        : `Starting full ${supplierName} sync — ${r.total} styles enumerated`);
      await poll();
    } catch (e) {
      showToast('Failed to start: ' + e.message, 'error');
    } finally {
      setStarting(false);
    }
  };

  const stop = async () => {
    setStopping(true);
    try {
      const t = await getToken();
      await api.withToken(t).stopFullCatalogSync(slug);
      showToast(`Stopping — will halt after the current batch`);
      await poll();
    } catch (e) {
      showToast('Failed to stop: ' + e.message, 'error');
    } finally {
      setStopping(false);
    }
  };

  const btn = (bg, color, border) => ({ background:bg, color, border:border||'none', borderRadius:8, padding:'8px 14px', fontSize:13, fontWeight:500, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' });

  const running   = status?.status === 'running';
  const pct       = status && status.total ? Math.min(100, Math.round((status.processed / status.total) * 100)) : 0;

  return (
    <div style={{ marginTop:14, paddingTop:14, borderTop:'1px dashed #E2E8F0' }}>
      <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:6 }}>Sync full catalog</div>
      <div style={{ fontSize:11, color:'#94A3B8', marginBottom:8, lineHeight:1.5 }}>
        Pulls every {supplierName} style into your catalog, not just the ones you type in above. Runs in the
        background in batches — you can leave this page and come back, and it's safe to click Start again if it
        was interrupted (it resumes instead of starting over).
      </div>

      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        {!running && (
          <button onClick={start} disabled={starting} style={btn(G, '#fff')}>
            {starting ? 'Starting…' : (status && status.status === 'stopped' ? 'Resume full sync' : 'Sync full catalog')}
          </button>
        )}
        {running && (
          <button onClick={stop} disabled={stopping} style={btn('#fff', '#B91C1C', '1px solid #FECACA')}>
            {stopping ? 'Stopping…' : 'Stop'}
          </button>
        )}
      </div>

      {status && status.status !== 'idle' && (
        <div style={{ marginTop:10 }}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#64748B', marginBottom:4 }}>
            <span>
              {running ? 'Syncing…' : status.status === 'completed' ? 'Completed' : status.status === 'stopped' ? 'Stopped' : status.status === 'failed' ? 'Failed' : ''}
              {' '}{status.processed || 0} / {status.total || 0} styles
            </span>
            <span>{pct}%</span>
          </div>
          <div style={{ height:6, borderRadius:4, background:'#F1F5F9', overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${pct}%`, background: status.status === 'failed' ? '#EF4444' : G, transition:'width 0.4s ease' }} />
          </div>
          <div style={{ fontSize:11, color:'#94A3B8', marginTop:6 }}>
            Synced {status.synced || 0}, failed {status.failed || 0}, skipped {status.skipped || 0}.
            {status.lastError && <span style={{ color:'#B91C1C' }}> Last error: {status.lastError}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// Passive progress panel for the automatic, connect-time pricing backfill —
// no start/stop buttons, since it's triggered by saving credentials, not by
// a user action here. Purely informational: makes the one-time background
// pull visible (so it doesn't look like nothing's happening) and explicit
// that it's one-time, not a recurring thing to expect.
function PricingBackfillStatus({ code, supplierName, getToken }) {
  const slug = CODE_TO_SLUG[code];
  const [status, setStatus] = useState(null);

  const poll = useCallback(async () => {
    try {
      const t = await getToken();
      const s = await api.withToken(t).getPricingBackfillStatus(slug);
      setStatus(s);
    } catch (e) {
      // Silent — same as FullCatalogSyncTool's poll, don't spam on a bad tick.
    }
  }, [slug, getToken]);

  useEffect(() => { poll(); }, [poll]);

  useEffect(() => {
    if (status?.status !== 'running') return;
    const id = setInterval(poll, 4000);
    return () => clearInterval(id);
  }, [status?.status, poll]);

  // Nothing to show until a backfill has actually started at least once
  // (e.g. right after connecting) — no point rendering an empty panel.
  if (!status || status.status === 'idle') return null;

  const running = status.status === 'running';
  const pct = status.total ? Math.min(100, Math.round((status.processed / status.total) * 100)) : 0;

  return (
    <div style={{ marginTop:14, paddingTop:14, borderTop:'1px dashed #E2E8F0' }}>
      <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>
        Your account's pricing
      </div>
      <div style={{ fontSize:11, color:'#94A3B8', marginBottom:8, lineHeight:1.5 }}>
        {running
          ? `Pulling your real ${supplierName} account pricing for every product — a one-time sync so search shows your own negotiated rate, not a shared placeholder. This won't repeat once it's done.`
          : status.status === 'completed'
            ? `Your ${supplierName} pricing is up to date. This ran once and stays current automatically going forward — no need to redo it.`
            : status.status === 'failed'
              ? `Pricing sync hit an error partway through. Reconnecting (re-save your credentials) will pick up where it left off.`
              : ''}
      </div>

      {running && (
        <>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#64748B', marginBottom:4 }}>
            <span>{status.processed || 0} / {status.total || 0} products</span>
            <span>{pct}%</span>
          </div>
          <div style={{ height:6, borderRadius:4, background:'#F1F5F9', overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${pct}%`, background:G, transition:'width 0.4s ease' }} />
          </div>
        </>
      )}

      {!running && (
        <div style={{ fontSize:11, color:'#94A3B8' }}>
          {status.status === 'completed' && `Synced ${status.synced || 0} of ${status.total || 0} products.`}
          {status.status === 'failed' && status.lastError && <span style={{ color:'#B91C1C' }}>{status.lastError}</span>}
        </div>
      )}
    </div>
  );
}
